import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Fortinet.css";
import fortinetLogo from "../../assets/images/partners/fortinet.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const fabricCards = [
  { cat: "NGFW", title: "FortiGate Next-Generation Firewall", img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=700&q=80", desc: "The world's most deployed NGFW — powered by purpose-built SPU ASICs delivering 300Gbps threat protection throughput with zero performance compromise. Available in physical, VM, cloud, and container form factors.", badges: ["NGFW", "IPS", "App Control", "SSL Inspection", "AI-Powered"] },
  { cat: "SASE & SD-WAN", title: "FortiSASE & FortiSD-WAN", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=700&q=80", desc: "Converge networking and security in a single cloud-delivered platform. FortiSASE enforces consistent zero-trust policy for remote users, while FortiSD-WAN delivers application-aware path selection across all WAN links.", badges: ["ZTNA", "SWG", "CASB", "Cloud Access", "SD-WAN"] },
  { cat: "NOC/SOC Platform", title: "FortiSIEM, FortiSOAR & FortiAnalyzer", img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=700&q=80", desc: "Unified security operations with AI-driven threat detection, automated investigation, and playbook-based response. FortiSIEM collects telemetry from 700+ data sources, while FortiSOAR automates the response workflow.", badges: ["SIEM", "SOAR", "UEBA", "Compliance", "Log Management"] },
  { cat: "Endpoint & XDR", title: "FortiEDR & FortiXDR", img: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?auto=format&fit=crop&w=700&q=80", desc: "Real-time endpoint protection that stops breaches before execution. FortiEDR's behavioral tracking and automatic mitigation responds to threats in milliseconds — without requiring a live connection to the cloud.", badges: ["EDR", "XDR", "Behavioral Analysis", "Auto-Remediation"] },
  { cat: "OT Security", title: "Fortinet OT/ICS Security", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=700&q=80", desc: "Purpose-built security for operational technology environments. FortiGate Rugged appliances and FortiOT provide segmentation, device discovery, and threat detection across industrial control systems and SCADA environments.", badges: ["OT/ICS", "SCADA", "Purdue Model", "Asset Discovery"] },
  { cat: "Cloud Security", title: "FortiCNP & Cloud Security", img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=700&q=80", desc: "Consistent security posture across AWS, Azure, and Google Cloud. FortiCNP unifies cloud native application protection, workload security, and container security with deep integration into cloud provider APIs.", badges: ["CSPM", "CWPP", "Container Security", "Multi-Cloud"] },
];

const ztPillars = [
  { title: "Verify Every Identity", desc: "FortiAuthenticator and Fortinet's ZTNA enforce multi-factor authentication and continuous identity verification for every user and device — before any resource access is granted." },
  { title: "Validate Every Device", desc: "Endpoint posture assessment through FortiClient confirms device health, compliance, and patch level at login and throughout the session — blocking non-compliant devices." },
  { title: "Enforce Least-Privilege Access", desc: "Application-level ZTNA access control replaces network-wide VPN — users access only specific applications they're authorized for, never the broader network." },
  { title: "Microsegment Everything", desc: "FortiGate-enforced micro-segmentation creates logical security zones that prevent lateral movement — containing breaches to the compromised segment only." },
  { title: "Monitor Continuously", desc: "FortiSIEM and FortiAnalyzer provide persistent behavioral monitoring, detecting anomalies across users, devices, and network flows throughout active sessions." },
];

const cloudCaps = ["FortiGate VM on AWS/Azure/GCP", "FortiCNP Cloud Security", "CSPM & Misconfiguration Detection", "Container & Kubernetes Security", "FortiCASB for SaaS Visibility", "Cloud API Integration (native)"];

const compliance = [
  { icon: "🏥", name: "HIPAA", desc: "PHI protection, access controls, and audit logging for healthcare organizations" },
  { icon: "💳", name: "PCI DSS", desc: "Cardholder data environment segmentation, logging, and intrusion prevention" },
  { icon: "🏛️", name: "NIST CSF", desc: "Complete mapping across Identify, Protect, Detect, Respond, and Recover functions" },
  { icon: "🔐", name: "ISO 27001", desc: "Information security management system controls and audit-ready evidence" },
  { icon: "📋", name: "SOC 2 Type II", desc: "Security, availability, and confidentiality controls with continuous monitoring" },
  { icon: "🌍", name: "GDPR", desc: "Data protection controls, breach detection, and privacy-by-design architecture" },
  { icon: "🛡️", name: "CMMC", desc: "Cybersecurity Maturity Model Certification for DoD contractor environments" },
  { icon: "⚡", name: "NERC CIP", desc: "Critical infrastructure protection for energy and utility sector environments" },
];

const industries = [
  { icon: "🏦", name: "Financial Services", desc: "Zero-trust network segmentation, real-time fraud detection with FortiDeceptor, and PCI-DSS compliant firewall policy for banking and trading environments." },
  { icon: "🏥", name: "Healthcare", desc: "Medical IoT device security, clinical network segmentation, HIPAA-compliant logging, and ransomware protection across EHR and connected device environments." },
  { icon: "🏭", name: "Manufacturing & OT", desc: "Fortinet's dedicated OT security platform protects industrial control systems, SCADA, and factory-floor devices with purpose-built ruggedized appliances." },
  { icon: "🏛️", name: "Government & Defense", desc: "CMMC-ready Fortinet deployment, classified network segmentation, and FortiAuthenticator for PIV/CAC authentication in federal and defense environments." },
  { icon: "🎓", name: "Education", desc: "Campus-wide network protection, student data security, web filtering, and ransomware prevention across high-density, high-risk network environments." },
  { icon: "⚡", name: "Energy & Utilities", desc: "NERC CIP compliance, SCADA network protection, and converged IT/OT security for power generation, grid management, and pipeline operations." },
];

const fortiguardStats = [
  { val: "1M+", label: "Threats Blocked / Hour" },
  { val: "650K+", label: "Active FortiGate Sensors" },
  { val: "100+", label: "Countries Monitored" },
  { val: "200+", label: "Threat Intelligence Feeds" },
];

const certs = [
  { icon: "🏆", name: "Fortinet Elite Partner", desc: "Elite-tier authorized reseller — highest Fortinet partner designation globally" },
  { icon: "🛡️", name: "NSE 7 — Enterprise Firewall", desc: "Expert-level certification in FortiGate enterprise firewall design and troubleshooting" },
  { icon: "🔍", name: "NSE 7 — SD-WAN", desc: "Expert-level Fortinet SD-WAN design, implementation, and operations certification" },
  { icon: "📊", name: "NSE 7 — FortiSIEM", desc: "Security operations and SIEM deployment certification for enterprise SOC environments" },
];

const faqData = [
  { q: "What is the Fortinet Security Fabric and how does it differ from point security solutions?", a: "The Fortinet Security Fabric is a tightly integrated cybersecurity architecture where every Fortinet product — firewalls, endpoints, SIEM, SASE, switches, and APs — shares threat intelligence, correlates events, and automates responses across the entire environment. Unlike point solutions that operate independently, the Security Fabric provides single-pane visibility across all security controls, dramatically reducing investigation time and enabling automated playbook responses that point tools cannot execute. ALG Data Guard designs Security Fabric implementations that replace multi-vendor security complexity with a unified Fortinet platform." },
  { q: "How does FortiGate's performance compare to competing NGFW platforms?", a: "FortiGate NGFW appliances use Fortinet's proprietary SPU (Security Processing Unit) ASICs — custom silicon that offloads compute-intensive security functions like SSL inspection, IPS, and deep packet inspection from the main CPU. This hardware acceleration allows FortiGate to deliver threat protection throughput at 3–10x the performance of competing platforms using general-purpose CPUs at the same price point. Enterprise FortiGate models deliver 300Gbps+ threat protection throughput — enabling SSL inspection and full security profile activation without performance compromise even at hyperscale." },
  { q: "What is Fortinet ZTNA and how does it replace VPN?", a: "Fortinet ZTNA (Zero Trust Network Access) provides application-level access control that grants users access to specific applications they're authorized for — never the entire network as traditional VPN does. ZTNA continuously verifies user identity, device health, and application entitlement throughout the session. Remote users connect through an encrypted tunnel to the FortiGate ZTNA proxy, which enforces policy and logs every access attempt. ALG Data Guard implements ZTNA alongside FortiClient for device posture assessment, providing a complete zero-trust remote access architecture that eliminates VPN attack surface." },
  { q: "How does ALG Data Guard support Fortinet in ongoing managed security operations?", a: "ALG Data Guard's managed Fortinet services include: 24/7 FortiGate firewall monitoring and policy management, FortiSIEM security event correlation and alert triage, monthly threat intelligence reporting from FortiGuard, firmware lifecycle management, configuration change management with audit trail, compliance reporting for PCI-DSS, HIPAA, and NIST CSF frameworks, and incident response coordination. We offer co-managed (augmenting your security team) and fully managed SOC-as-a-Service models powered by Fortinet's Security Fabric." },
  { q: "What is FortiGuard Labs and why does it matter for threat intelligence?", a: "FortiGuard Labs is Fortinet's global threat intelligence and research organization — one of the world's largest, with over 200 dedicated researchers monitoring 3.5+ million sensors globally and analyzing 100+ billion security events daily. FortiGuard provides real-time threat intelligence updates to all FortiGate, FortiClient, and FortiSandbox deployments — ensuring protection against zero-day threats, emerging attack campaigns, and botnet command-and-control infrastructure within minutes of discovery. This live intelligence is what powers FortiGate's AI-enhanced IPS signatures, web category databases, and anti-malware engines." },
];

const tickerItems = [
  "3.5M+ FortiGuard Sensors Active",
  "100B+ Security Events Analyzed Daily",
  "150+ Zero-Day Vulnerabilities Discovered",
  "200+ Threat Intelligence Feeds",
  "1M+ Threats Blocked Per Hour",
  "FortiGate — #1 NGFW by Market Share",
  "Coverage in 150+ Countries",
  "650K+ Active FortiGate Deployments",
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Fortinet() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(650, 2200, metricsInView);
  const c2 = useCounter(99, 2000, metricsInView);
  const c3 = useCounter(100, 2000, metricsInView);
  const c4 = useCounter(350, 2200, metricsInView);

  return (
    <div className="fn-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={fortinetLogo} alt="Fortinet" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Cybersecurity Platform Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Fortinet</h1>
            <p className="pd-identity-desc">
              Fortinet delivers the world's most comprehensive cybersecurity platform — the Security Fabric — protecting every edge, endpoint, and cloud workload with unified threat intelligence from FortiGuard Labs. ALG Data Guard is a Fortinet Elite Partner, deploying and managing the full Fortinet platform for enterprise and mid-market organizations across every industry.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="fn-hero">
        <div className="fn-hero__scan-lines" aria-hidden="true" />
        <div className="fn-hero__glow-left" aria-hidden="true" />
        <div className="fn-hero__inner">
          <div>
            <div className="fn-threat-tag">
              <span className="fn-blink" />
              FORTIGUARD ACTIVE — THREAT INTELLIGENCE LIVE
            </div>
            <h2 className="fn-hero__title">
              Threat Actors Don't Sleep.
              <span className="fn-hero__title-red">Neither Does Fortinet.</span>
            </h2>
            <p className="fn-hero__body">
              The Fortinet Security Fabric delivers integrated, AI-powered cybersecurity across your entire attack surface — from endpoint to edge to cloud — enforcing consistent policy and sharing threat intelligence across every control point. ALG Data Guard deploys and operates the full Security Fabric so your team can focus on the business.
            </p>
            <div className="fn-hero__actions">
              <button className="fn-btn fn-btn--red" onClick={() => navigate("/contact")}>
                Assess My Security Posture <ArrowIcon />
              </button>
              <button className="fn-btn fn-btn--ghost" onClick={() => navigate("/contact")}>
                Talk to a Security Expert
              </button>
            </div>
            <div className="fn-hero__threat-board">
              <div className="fn-hero__threat-stat">
                <span className="fn-hero__threat-val">1M+</span>
                <span className="fn-hero__threat-label">Threats Blocked / Hour</span>
              </div>
              <div className="fn-hero__threat-stat">
                <span className="fn-hero__threat-val">3.5M+</span>
                <span className="fn-hero__threat-label">Active Global Sensors</span>
              </div>
              <div className="fn-hero__threat-stat">
                <span className="fn-hero__threat-val">100B+</span>
                <span className="fn-hero__threat-label">Events / Day Analyzed</span>
              </div>
            </div>
          </div>

          {/* SOC Dashboard Card */}
          <div className="fn-soc-card">
            <div className="fn-soc-card__header">
              <span className="fn-soc-card__header-dot" />
              FORTIGUARD THREAT CONSOLE — LIVE FEED
            </div>
            <div className="fn-soc-card__body">
              <div className="fn-threat-row fn-threat-row--critical">
                <span className="fn-threat-row__label">CRITICAL — Ransomware Attempt</span>
                <span className="fn-threat-row__val-red">BLOCKED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--high">
                <span className="fn-threat-row__label">HIGH — C2 Callback Detected</span>
                <span className="fn-threat-row__val-orange">QUARANTINED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--high">
                <span className="fn-threat-row__label">HIGH — Lateral Movement Attempt</span>
                <span className="fn-threat-row__val-orange">BLOCKED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--med">
                <span className="fn-threat-row__label">MEDIUM — Phishing Email</span>
                <span className="fn-threat-row__val-yellow">FILTERED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--med">
                <span className="fn-threat-row__label">MEDIUM — Anomalous Login</span>
                <span className="fn-threat-row__val-yellow">MFA ENFORCED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--blocked">
                <span className="fn-threat-row__label">INFO — DLP Policy Triggered</span>
                <span className="fn-threat-row__val-green">LOGGED</span>
              </div>
              <div className="fn-threat-row fn-threat-row--blocked">
                <span className="fn-threat-row__label">COMPLIANCE — Audit Log Written</span>
                <span className="fn-threat-row__val-green">OK</span>
              </div>
            </div>
            <div className="fn-soc-card__footer">
              FortiSIEM + FortiSOAR — ALG Data Guard Managed SOC — All Threats Contained
            </div>
          </div>
        </div>
      </section>

      {/* ── Threat Ticker ── */}
      <div className="fn-threat-band">
        <div className="fn-threat-ticker">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <div key={i} className="fn-threat-ticker__item">{item}</div>
          ))}
        </div>
      </div>

      {/* ── Security Fabric Grid ── */}
      <section className="fn-fabric">
        <div className="fn-shell">
          <div className="fn-fabric__head">
            <div className="fn-threat-tag" style={{ margin:"0 auto 1rem" }}>
              <span className="fn-blink" /> Fortinet Security Fabric
            </div>
            <h2 className="fn-section-title" style={{ textAlign:"center" }}>One Platform. Every Edge. Zero Gaps.</h2>
            <p className="fn-section-sub fn-fabric__sub-text" style={{ textAlign:"center" }}>
              Unlike bolt-together multi-vendor security stacks, the Fortinet Security Fabric shares threat intelligence, automates responses, and provides single-pane visibility across every security layer — simultaneously.
            </p>
          </div>
          <div className="fn-fabric__grid">
            {fabricCards.map(card => (
              <div key={card.title} className="fn-fabric-card">
                <div className="fn-fabric-card__img-wrap">
                  <img src={card.img} alt={card.title} className="fn-fabric-card__img" />
                  <span className="fn-fabric-card__cat">{card.cat}</span>
                </div>
                <div className="fn-fabric-card__body">
                  <h3 className="fn-fabric-card__title">{card.title}</h3>
                  <p className="fn-fabric-card__desc">{card.desc}</p>
                  <div className="fn-fabric-card__badges">{card.badges.map(b => <span key={b} className="fn-fabric-card__badge">{b}</span>)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Zero Trust ── */}
      <section className="fn-zt">
        <div className="fn-shell">
          <div className="fn-zt__inner">
            <div>
              <div className="fn-threat-tag"><span className="fn-blink" /> Zero Trust Architecture</div>
              <h2 className="fn-section-title">Never Trust. Always Verify.<br/>Fortinet ZTNA.</h2>
              <p className="fn-section-sub">
                Traditional perimeter security assumes anything inside the network is safe. Zero trust assumes breach — verifying every identity, validating every device, and enforcing least-privilege access for every session. Fortinet's ZTNA is integrated across the Security Fabric, not bolted on.
              </p>
              <div className="fn-zt__pillars">
                {ztPillars.map(p => (
                  <div key={p.title} className="fn-zt__pillar">
                    <div className="fn-zt__pillar-title">{p.title}</div>
                    <p className="fn-zt__pillar-desc">{p.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2.5rem" }}>
                <button className="fn-btn fn-btn--red" onClick={() => navigate("/contact")}>
                  Design My Zero Trust Strategy <ArrowIcon />
                </button>
              </div>
            </div>
            <div className="fn-zt__img-stack">
              <img
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=900&q=80"
                alt="Zero Trust Security Operations"
                className="fn-zt__img"
              />
              <div className="fn-zt__img-overlay" />
              <div className="fn-zt__img-stat">
                <span className="fn-zt__img-stat-val">95%</span>
                <span className="fn-zt__img-stat-label">Breach prevention rate with ZTNA</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cloud Security ── */}
      <section className="fn-cloud">
        <div className="fn-shell">
          <div className="fn-cloud__inner">
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="Cloud security operations"
              className="fn-cloud__img"
            />
            <div>
              <div className="fn-threat-tag"><span className="fn-blink" /> Cloud Security</div>
              <h2 className="fn-section-title">Consistent Security Across Every Cloud</h2>
              <p className="fn-section-sub">
                Cloud workloads don't inherently come with enterprise-grade security. Fortinet's cloud security portfolio delivers the same FortiGate NGFW policy enforcement in AWS, Azure, and Google Cloud that you run on-premises — with FortiCNP providing continuous cloud security posture management and misconfiguration detection across your entire cloud estate.
              </p>
              <div className="fn-cloud__caps">
                {cloudCaps.map(c => <span key={c} className="fn-cloud__cap">{c}</span>)}
              </div>
              <button className="fn-btn fn-btn--red" onClick={() => navigate("/contact")}>
                Assess My Cloud Security <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="fn-compliance">
        <div className="fn-shell">
          <div className="fn-compliance__head">
            <div className="fn-threat-tag" style={{ margin:"0 auto 1rem" }}><span className="fn-blink" /> Compliance Frameworks</div>
            <h2 className="fn-section-title" style={{ textAlign:"center" }}>Fortinet Covers Every Major Compliance Mandate</h2>
            <p className="fn-section-sub" style={{ textAlign:"center", margin:"0 auto" }}>
              ALG Data Guard maps Fortinet Security Fabric controls to your specific compliance requirements — with automated reporting and audit-ready evidence packages.
            </p>
          </div>
          <div className="fn-compliance__grid">
            {compliance.map(c => (
              <div key={c.name} className="fn-compliance-card">
                <span className="fn-compliance-card__icon">{c.icon}</span>
                <div className="fn-compliance-card__name">{c.name}</div>
                <p className="fn-compliance-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="fn-industries">
        <div className="fn-shell">
          <div className="fn-industries__head">
            <div className="fn-threat-tag"><span className="fn-blink" /> Industry Solutions</div>
            <h2 className="fn-section-title">Fortinet Security Across Every Sector</h2>
            <p className="fn-section-sub">
              Every industry faces unique threat vectors, compliance mandates, and network environments. ALG Data Guard designs Fortinet Security Fabric implementations tailored to your sector's specific requirements.
            </p>
          </div>
          <div className="fn-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="fn-industry-card">
                <span className="fn-industry-card__icon">{ind.icon}</span>
                <div>
                  <div className="fn-industry-card__name">{ind.name}</div>
                  <p className="fn-industry-card__desc">{ind.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FortiGuard Intelligence ── */}
      <section className="fn-fortiguard">
        <div className="fn-shell">
          <div className="fn-fortiguard__inner">
            <div>
              <div className="fn-threat-tag"><span className="fn-blink" /> FortiGuard Labs</div>
              <h2 className="fn-section-title">The World's Largest Threat Intelligence Organization</h2>
              <p className="fn-section-sub">
                FortiGuard Labs is the cyber threat intelligence engine behind the entire Fortinet Security Fabric. With over 200 dedicated researchers, FortiGuard analyzes 100+ billion events daily across 3.5+ million sensors — discovering zero-days, tracking APT groups, and delivering real-time intelligence updates to every FortiGate and FortiClient device globally within minutes of threat discovery.
              </p>
              <div className="fn-fortiguard__stats">
                {fortiguardStats.map(s => (
                  <div key={s.val} className="fn-fg-stat">
                    <span className="fn-fg-stat__val">{s.val}</span>
                    <span className="fn-fg-stat__label">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=900&q=80"
              alt="FortiGuard threat intelligence"
              className="fn-fortiguard__img"
            />
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="fn-metrics" ref={metricsRef}>
        <div className="fn-shell">
          <div className="fn-metrics__inner">
            <div><div className="fn-metric__val">{c1}K+</div><div className="fn-metric__label">FortiGate Deployments</div></div>
            <div><div className="fn-metric__val">{c2}%</div><div className="fn-metric__label">Threat Block Rate</div></div>
            <div><div className="fn-metric__val">{c3}+</div><div className="fn-metric__label">Countries Covered</div></div>
            <div><div className="fn-metric__val">{c4}+</div><div className="fn-metric__label">Enterprise Clients</div></div>
          </div>
        </div>
      </section>

      {/* ── Certifications ── */}
      <section className="fn-certs">
        <div className="fn-shell">
          <div className="fn-certs__head">
            <div className="fn-threat-tag" style={{ margin:"0 auto 1rem" }}><span className="fn-blink" /> Partner Credentials</div>
            <h2 className="fn-section-title" style={{ textAlign:"center" }}>Certified Fortinet Security Engineers</h2>
            <p className="fn-section-sub" style={{ textAlign:"center", margin:"0 auto" }}>
              ALG Data Guard's security team holds NSE-level certifications across the Fortinet Security Fabric — ensuring every deployment is executed to expert standards.
            </p>
          </div>
          <div className="fn-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="fn-cert-card">
                <span className="fn-cert-card__icon">{c.icon}</span>
                <div className="fn-cert-card__name">{c.name}</div>
                <p className="fn-cert-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="fn-faq">
        <div className="fn-shell">
          <div className="fn-faq__head">
            <div className="fn-threat-tag" style={{ margin:"0 auto 1rem" }}><span className="fn-blink" /> FAQs</div>
            <h2 className="fn-section-title" style={{ textAlign:"center" }}>Fortinet Security Questions, Answered</h2>
            <p className="fn-section-sub" style={{ textAlign:"center", margin:"0 auto" }}>
              Expert answers about the Fortinet Security Fabric and ALG Data Guard's managed security services.
            </p>
          </div>
          <div className="fn-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`fn-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="fn-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="fn-faq-item__q">{faq.q}</span>
                  <span className="fn-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="fn-faq-item__answer"><div className="fn-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="fn-cta">
        <div className="fn-cta__inner">
          <div className="fn-threat-tag" style={{ margin:"0 auto 1.2rem" }}><span className="fn-blink" /> Fortify Your Defenses</div>
          <h2 className="fn-cta__title">Stop Threats Before They Stop Your Business</h2>
          <p className="fn-cta__sub">
            ALG Data Guard's Fortinet Elite-certified security engineers assess your current posture, design a Security Fabric architecture for your environment, and manage it 24/7 so threats are contained before they reach your critical systems.
          </p>
          <div className="fn-cta__actions">
            <button className="fn-btn fn-btn--red" onClick={() => navigate("/contact")}>
              Request Security Assessment <ArrowIcon />
            </button>
            <button className="fn-btn fn-btn--ghost" onClick={() => navigate("/contact")}>
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
